import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { type ApiKey, api, type Domain, type EmailStatus, type EmailSummary } from '../api';
import { CheckIcon, DownloadIcon, SearchIcon, SendIcon } from '../components/icons';
import {
  CodeBlock,
  CodeButton,
  downloadCsv,
  ErrorNote,
  formatDate,
  label,
  PageHeader,
  SkeletonRows,
  Status,
  timeAgo,
  useDebounced,
} from '../components/ui';
import { useLoad } from '../hooks';

const STATUSES: EmailStatus[] = [
  'delivered',
  'sent',
  'scheduled',
  'delivery_delayed',
  'bounced',
  'complained',
  'suppressed',
  'failed',
  'canceled',
];

const RANGES = [
  { days: 1, label: 'Last 24 hours' },
  { days: 7, label: 'Last 7 days' },
  { days: 15, label: 'Last 15 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

const TEST_EMAIL = `curl -X POST https://api.vaartix.com/emails \\
  -H "Authorization: Bearer $VAARTIX_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "Acme <hello@test.vaartix.com>",
    "to": "delivered@test.vaartix.com",
    "subject": "Hello from Vaartix",
    "text": "It works!"
  }'`;

const SNIPPETS = [
  {
    label: 'Node.js',
    code: `import { Vaartix } from 'vaartix';

const vaartix = new Vaartix(process.env.VAARTIX_API_KEY);

// Send
await vaartix.emails.send({
  from: 'Acme <orders@acme.in>',
  to: 'priya@example.com',
  subject: 'Your order is confirmed',
  html: '<p>Thanks for your order!</p>',
});

// List recent bounces
const { data } = await vaartix.emails.list({ limit: 20 });`,
  },
  {
    label: 'cURL',
    code: `curl "https://api.vaartix.com/emails?status=bounced&days=7" \\
  -H "Authorization: Bearer $VAARTIX_API_KEY"`,
  },
];

type Page = { data: EmailSummary[]; hasMore: boolean };

/** First run: what to do to send a first email, with progress ticks. */
function GettingStarted({ keys, domains }: { keys: ApiKey[]; domains: Domain[] }) {
  const hasKey = keys.length > 0;
  const hasDomain = domains.some((d) => d.status === 'verified');
  return (
    <div className="empty">
      <span className="tile">
        <SendIcon />
      </span>
      <h3>Send your first email</h3>
      <p>Three steps, about five minutes. Test addresses work before your domain is verified.</p>
      <div className="steps">
        <div className={`step-card${hasKey ? ' done' : ''}`}>
          <span className="n">{hasKey ? <CheckIcon /> : 1}</span>
          <h4>Create an API key</h4>
          <p>Your server uses it to send. You'll see it once, so store it safely.</p>
          {hasKey ? null : (
            <Link to="/api-keys" className="btn btn-primary btn-sm">
              Create key
            </Link>
          )}
        </div>
        <div className="step-card">
          <span className="n">2</span>
          <h4>Send a test email</h4>
          <p>
            Send to <code>delivered@</code>, <code>bounced@</code> or{' '}
            <code>complained@test.vaartix.com</code> to see each outcome. Nothing real is sent.
          </p>
        </div>
        <div className={`step-card${hasDomain ? ' done' : ''}`}>
          <span className="n">{hasDomain ? <CheckIcon /> : 3}</span>
          <h4>Verify your domain</h4>
          <p>Add a few DNS records so you can send from your own address.</p>
          {hasDomain ? null : (
            <Link to="/domains/new" className="btn btn-ghost btn-sm">
              Add domain
            </Link>
          )}
        </div>
      </div>
      <div style={{ maxWidth: 640, margin: '20px auto 0' }}>
        <CodeBlock code={TEST_EMAIL} />
      </div>
    </div>
  );
}

export function Emails() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(15);
  const [status, setStatus] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const q = useDebounced(search.trim());
  const [older, setOlder] = useState<EmailSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = (before?: string) => {
    const p = new URLSearchParams({ limit: '50', days: String(days) });
    if (q) p.set('search', q);
    if (status) p.set('status', status);
    if (apiKeyId) p.set('apiKeyId', apiKeyId);
    if (before) p.set('before', before);
    return `/emails?${p}`;
  };

  const first = useLoad(async () => {
    const res = await api<Page>('GET', query());
    setOlder([]);
    setHasMore(res.hasMore);
    return res.data;
  }, [q, days, status, apiKeyId]);
  const keys = useLoad(() => api<{ data: ApiKey[] }>('GET', '/api-keys'), []);
  const domains = useLoad(() => api<{ data: Domain[] }>('GET', '/domains'), []);

  const emails = [...(first.data ?? []), ...older];
  const filtered = Boolean(q || status || apiKeyId || days !== 15);
  const brandNew = !first.loading && emails.length === 0 && !filtered && keys.data && domains.data;

  async function loadMore() {
    const last = emails.at(-1);
    if (!last) return;
    setLoadingMore(true);
    try {
      const res = await api<Page>('GET', query(last.id));
      setOlder((o) => [...o, ...res.data]);
      setHasMore(res.hasMore);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Emails"
        description="Every email your team sent, with its delivery status."
        actions={
          <>
            <Link to="/emails/new" className="btn btn-primary">
              <SendIcon />
              Send test email
            </Link>
            <CodeButton title="Send and list emails" snippets={SNIPPETS} />
          </>
        }
      />
      <ErrorNote error={first.error} />

      {brandNew ? (
        <GettingStarted keys={keys.data?.data ?? []} domains={domains.data?.data ?? []} />
      ) : (
        <>
          <div className="filters">
            <div className="search">
              <SearchIcon />
              <input
                className="input"
                type="search"
                placeholder="Search by recipient, sender or subject…"
                aria-label="Search emails"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="input"
              aria-label="Time range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {RANGES.map((r) => (
                <option key={r.days} value={r.days}>
                  {r.label}
                </option>
              ))}
            </select>
            <select
              className="input"
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {label(s)}
                </option>
              ))}
            </select>
            <select
              className="input hide-sm"
              aria-label="API key"
              value={apiKeyId}
              onChange={(e) => setApiKeyId(e.target.value)}
            >
              <option value="">All API keys</option>
              {(keys.data?.data ?? []).map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-ghost btn-icon hide-sm"
              aria-label="Export as CSV"
              title="Export as CSV"
              disabled={emails.length === 0}
              onClick={() =>
                downloadCsv(
                  'vaartix-emails.csv',
                  emails.map((e) => ({
                    id: e.id,
                    to: e.to,
                    from: e.from,
                    subject: e.subject,
                    status: e.status,
                    sentAt: e.sentAt ?? '',
                    createdAt: e.createdAt,
                  })),
                )
              }
            >
              <DownloadIcon />
            </button>
          </div>

          {!first.loading && emails.length === 0 ? (
            <div className="empty" style={{ padding: '56px 24px' }}>
              <span className="tile">
                <SearchIcon />
              </span>
              <h3>No emails match</h3>
              <p>Try another search, a longer time range or a different status.</p>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setSearch('');
                    setStatus('');
                    setApiKeyId('');
                    setDays(15);
                  }}
                >
                  Clear filters
                </button>
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>To</th>
                    <th>Status</th>
                    <th className="hide-sm">Subject</th>
                    <th className="nowrap hide-sm">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {first.loading ? (
                    <SkeletonRows cols={4} />
                  ) : (
                    emails.map((email) => {
                      const extra = email.to.length + email.cc.length + email.bcc.length - 1;
                      return (
                        <tr
                          key={email.id}
                          className="row-link"
                          tabIndex={0}
                          onClick={() => navigate(`/emails/${email.id}`)}
                          onKeyDown={(e) => e.key === 'Enter' && navigate(`/emails/${email.id}`)}
                        >
                          <td className="truncate">
                            <div className="cell-main">
                              <span className="row-icon">
                                <SendIcon />
                              </span>
                              <span className="text">
                                <strong>
                                  {email.to[0]}
                                  {extra > 0 ? <span className="dim"> +{extra}</span> : null}
                                </strong>
                                <span className="sub show-sm">{email.subject}</span>
                              </span>
                            </div>
                          </td>
                          <td>
                            <Status status={email.status} />
                          </td>
                          <td className="truncate hide-sm muted">{email.subject}</td>
                          <td className="nowrap hide-sm muted" title={formatDate(email.createdAt)}>
                            {email.status === 'scheduled' ? (
                              <>
                                {formatDate(email.scheduledAt)}
                                <span className="sub">Scheduled</span>
                              </>
                            ) : (
                              timeAgo(email.sentAt ?? email.createdAt)
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          {emails.length > 0 ? (
            <div className="table-foot">
              <span>
                {emails.length} email{emails.length === 1 ? '' : 's'}
                {hasMore ? ' loaded' : ''}
              </span>
              {hasMore ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={loadingMore}
                  onClick={() => void loadMore()}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
